import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Configurar interceptor de axios para agregar token automáticamente
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            if (userData.token) {
              config.headers.Authorization = `Bearer ${userData.token}`;
            }
          } catch (error) {
            console.error('Error parsing user for token:', error);
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Configurar interceptor para manejar respuestas y detectar tokens vencidos (401)
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response && error.response.status === 401) {
          console.warn('Token expirado o no autorizado. Haciendo logout automático...');
          localStorage.removeItem('user');
          setUser(null);
          if (window.location.pathname !== '/login') {
            const currentPath = window.location.pathname + window.location.search + window.location.hash;
            window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
          }
        }
        return Promise.reject(error);
      }
    );

    // Cleanup: remover interceptores cuando se desmonte el componente
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Cargar usuario al iniciar
  useEffect(() => {
    // Limpiar datos antiguos de autenticación si existen
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');

    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        // Validar que tenga token, si no, limpiar sesión antigua
        if (userData.token) {
          setUser(userData);
        } else {
          // Sesión antigua sin token, limpiar
          console.log('Limpiando sesión antigua sin token');
          localStorage.removeItem('user');
        }
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // Monitorear expiración del token activamente
  useEffect(() => {
    if (!user || !user.token) return;

    try {
      // El token JWT es un string "header.payload.signature"
      const payloadBase64Url = user.token.split('.')[1];
      if (!payloadBase64Url) return;

      // Convertir Base64Url a Base64 estándar
      let base64 = payloadBase64Url.replace(/-/g, '+').replace(/_/g, '/');
      // Padding
      while (base64.length % 4) {
        base64 += '=';
      }

      const payloadString = atob(base64);
      const payload = JSON.parse(payloadString);

      // Si no hay fecha de expiración, no hacemos nada
      if (!payload.exp) return;

      const checkExpiration = () => {
        const currentTime = Math.floor(Date.now() / 1000);
        if (payload.exp <= currentTime) {
          console.warn('Token expirado por inactividad. Haciendo logout automático...');
          setUser(null);
          localStorage.removeItem('user');
          if (window.location.pathname !== '/login') {
            const currentPath = window.location.pathname + window.location.search + window.location.hash;
            window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
          }
        }
      };

      // Revisar inmediatamente al montar/actualizar
      checkExpiration();

      // Revisar periódicamente cada 60 segundos
      const intervalId = setInterval(checkExpiration, 120000);

      // Limpiar el intervalo cuando cambie el estado de usuario o se desmonte
      return () => clearInterval(intervalId);
    } catch (error) {
      console.error('Error al monitorear expiración del token:', error);
    }
  }, [user]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const userData = response.data;

      // Guardar usuario con token
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Error al iniciar sesión'
      };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
